'use client';

import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, FileUp, Plus, Save, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { useControls } from '@/features/controls/api/use-controls';
import { DEFAULT_CONTROL_FILTERS } from '@/features/controls/types';
import type { Control, ControlFilters } from '@/features/controls/types';
import { useMembers } from '@/features/team/api/use-members';
import type { OrganizationMember } from '@/features/team/types';
import {
  useAwarenessCampaign,
  useUpdateAwarenessCampaign,
} from '@/features/workforce-readiness/api/use-awareness-campaigns';
import { LinkedEvidencePanel } from '@/features/workforce-readiness/components/LinkedEvidencePanel';
import type {
  AwarenessCampaign,
  CampaignResourceLink,
  CampaignScheduleItem,
  CampaignStatus,
  ParticipationStatus,
} from '@/features/workforce-readiness/data';
import {
  buildAwarenessCampaignInput,
  mapAwarenessCampaignFromApi,
} from '@/features/workforce-readiness/mappers';
import { useOrganizations } from '@/hooks/use-organizations';
import i18n from '@/lib/i18n';

interface ParticipantStateDraft {
  participationStatus: ParticipationStatus;
  result: string;
}

interface CampaignWorkspaceDraft {
  campaignName: string;
  campaignType: string;
  description: string;
  status: CampaignStatus;
  startDate: string;
  endDate: string;
  selectedParticipantIds: string[];
  participantState: Record<string, ParticipantStateDraft>;
  selectedControlIds: string[];
  scheduleItems: CampaignScheduleItem[];
  resourceLinks: CampaignResourceLink[];
}

export const Route = createFileRoute(
  '/_authenticated/workforce-readiness/awareness-campaigns/$campaignId'
)({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.workforceReadinessAwarenessWorkspace'),
  }),
  component: AwarenessCampaignWorkspacePage,
});

function AwarenessCampaignWorkspacePage() {
  const { t } = useTranslation('common');
  const { campaignId } = Route.useParams();
  const navigate = useNavigate();
  const campaignQuery = useAwarenessCampaign(campaignId);
  const campaign = useMemo(
    () => (campaignQuery.data ? mapAwarenessCampaignFromApi(campaignQuery.data) : null),
    [campaignQuery.data]
  );
  const updateCampaignMutation = useUpdateAwarenessCampaign();

  const [draft, setDraft] = useState<CampaignWorkspaceDraft | null>(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveNotice, setSaveNotice] = useState<string | null>(null);
  const [participantSearch, setParticipantSearch] = useState('');
  const [controlSearch, setControlSearch] = useState('');

  const { organizations, isLoading: orgsLoading } = useOrganizations();
  const activeOrg = organizations.find((org) => org.isActive);
  const canManageAssignments = Boolean(activeOrg?.id);

  const membersQueryOptions = useMemo(
    () => ({
      orgId: activeOrg?.id ?? '',
      page: 1,
      pageSize: 300,
      sortBy: 'name' as const,
      sortOrder: 'asc' as const,
      enabled: Boolean(activeOrg?.id),
    }),
    [activeOrg?.id]
  );
  const { data: membersData, isLoading: membersLoading } = useMembers(membersQueryOptions);
  const availableMembers = useMemo(() => membersData?.members ?? [], [membersData?.members]);

  const controlsFilters = useMemo<ControlFilters>(
    () => ({
      ...DEFAULT_CONTROL_FILTERS,
      sortBy: 'title',
      sortOrder: 'asc',
    }),
    []
  );

  const { controls: availableControls, isLoading: controlsLoading } = useControls({
    filters: controlsFilters,
    page: 1,
    pageSize: 300,
    enabled: Boolean(activeOrg?.id),
  });

  const membersByUserId = useMemo(
    () => new Map(availableMembers.map((member) => [member.userId, member])),
    [availableMembers]
  );
  const controlsById = useMemo(
    () => new Map(availableControls.map((control) => [control.id, control])),
    [availableControls]
  );

  useEffect(() => {
    if (!campaign) {
      setDraft(null);
      return;
    }
    setDraft(buildCampaignWorkspaceDraft(campaign));
    setSaveError(null);
    setSaveNotice(null);
    setParticipantSearch('');
    setControlSearch('');
  }, [campaign]);

  const filteredMembers = useMemo(() => {
    const query = participantSearch.trim().toLowerCase();
    if (!query) {
      return availableMembers;
    }
    return availableMembers.filter((member) => {
      const displayName = getMemberDisplayName(member).toLowerCase();
      const email = member.email.toLowerCase();
      return displayName.includes(query) || email.includes(query);
    });
  }, [availableMembers, participantSearch]);

  const filteredControls = useMemo(() => {
    const query = controlSearch.trim().toLowerCase();
    if (!query) {
      return availableControls;
    }
    return availableControls.filter((control) => {
      const title = control.title.toLowerCase();
      const code = control.controlCode.toLowerCase();
      return title.includes(query) || code.includes(query);
    });
  }, [availableControls, controlSearch]);

  const isDirty = useMemo(() => {
    if (!campaign || !draft) {
      return false;
    }
    return (
      JSON.stringify(toComparableCampaign(campaign)) !==
      JSON.stringify(toComparableCampaignDraft(draft))
    );
  }, [campaign, draft]);

  const updateDraft = (updater: (current: CampaignWorkspaceDraft) => CampaignWorkspaceDraft) => {
    setDraft((current) => (current ? updater(current) : current));
    setSaveNotice(null);
  };

  const upsertParticipantSelection = (userId: string, checked: boolean) => {
    updateDraft((current) => {
      const selectedParticipantIds = checked
        ? uniqueValues([...current.selectedParticipantIds, userId])
        : current.selectedParticipantIds.filter((id) => id !== userId);

      if (checked) {
        const participantState = {
          ...current.participantState,
        };
        participantState[userId] ??= {
          participationStatus: 'pending',
          result: 'Pending',
        };

        return {
          ...current,
          selectedParticipantIds,
          participantState,
        };
      }

      const participantState = Object.fromEntries(
        Object.entries(current.participantState).filter(([id]) => id !== userId)
      ) as Record<string, ParticipantStateDraft>;

      return {
        ...current,
        selectedParticipantIds,
        participantState,
      };
    });
  };

  const updateParticipantState = (userId: string, patch: Partial<ParticipantStateDraft>) => {
    updateDraft((current) => {
      const previousState = current.participantState[userId] ?? {
        participationStatus: 'pending' as const,
        result: 'Pending',
      };

      return {
        ...current,
        participantState: {
          ...current.participantState,
          [userId]: {
            ...previousState,
            ...patch,
          },
        },
      };
    });
  };

  const upsertControlSelection = (controlId: string, checked: boolean) => {
    updateDraft((current) => ({
      ...current,
      selectedControlIds: checked
        ? uniqueValues([...current.selectedControlIds, controlId])
        : current.selectedControlIds.filter((id) => id !== controlId),
    }));
  };

  const updateScheduleItem = (scheduleId: string, patch: Partial<CampaignScheduleItem>) => {
    updateDraft((current) => ({
      ...current,
      scheduleItems: current.scheduleItems.map((item) =>
        item.id === scheduleId
          ? {
              ...item,
              ...patch,
            }
          : item
      ),
    }));
  };

  const updateResourceLink = (linkId: string, patch: Partial<CampaignResourceLink>) => {
    updateDraft((current) => ({
      ...current,
      resourceLinks: current.resourceLinks.map((item) =>
        item.id === linkId
          ? {
              ...item,
              ...patch,
            }
          : item
      ),
    }));
  };

  const saveCampaign = async () => {
    if (!campaign || !draft) {
      return;
    }

    const campaignName = draft.campaignName.trim();
    const campaignType = draft.campaignType.trim();
    if (!campaignName) {
      setSaveError('Campaign name is required.');
      return;
    }
    if (!campaignType) {
      setSaveError('Campaign type is required.');
      return;
    }
    if (!draft.startDate || !draft.endDate) {
      setSaveError('Start and end dates are required.');
      return;
    }
    if (draft.endDate < draft.startDate) {
      setSaveError('End date must be on or after start date.');
      return;
    }

    const nextCampaign: AwarenessCampaign = {
      ...campaign,
      campaignName,
      campaignType,
      description: draft.description.trim(),
      status: draft.status,
      startDate: draft.startDate,
      endDate: draft.endDate,
      participants: draft.selectedParticipantIds.map((userId) => {
        const participant = draft.participantState[userId] ?? {
          participationStatus: 'pending' as const,
          result: 'Pending',
        };
        return {
          userId,
          participationStatus: participant.participationStatus,
          result: participant.result.trim() || 'Pending',
        };
      }),
      linkedControlIds: uniqueValues(draft.selectedControlIds),
      scheduleItems: draft.scheduleItems
        .map((item) => ({
          ...item,
          title: item.title.trim(),
          activityDate: item.activityDate.trim(),
          channel: item.channel.trim(),
          notes: item.notes.trim(),
        }))
        .filter((item) => item.title.length > 0),
      resourceLinks: draft.resourceLinks
        .map((item) => ({
          ...item,
          label: item.label.trim(),
          url: item.url.trim(),
        }))
        .filter((item) => item.label.length > 0 && item.url.length > 0),
    };

    try {
      await updateCampaignMutation.mutateAsync({
        campaignId: campaign.id,
        data: buildAwarenessCampaignInput(nextCampaign),
      });
      setSaveError(null);
      setSaveNotice(t('workforceReadiness.campaignWorkspace.savedNotice'));
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Failed to save campaign.');
    }
  };

  if (orgsLoading || campaignQuery.isLoading) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-[1300px] space-y-4">
          <div className="h-10 w-48 animate-pulse rounded bg-muted" />
          <div className="h-48 animate-pulse rounded bg-muted" />
          <div className="h-96 animate-pulse rounded bg-muted" />
        </div>
      </div>
    );
  }

  if (campaignQuery.isError) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-3xl">
            <Card>
              <CardContent className="py-6 text-sm text-destructive">
                {campaignQuery.error.message || 'Failed to load awareness workspace.'}
              </CardContent>
            </Card>
          </div>
      </div>
    );
  }

  if (!campaign || !draft) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-3xl space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('workforceReadiness.campaignWorkspace.campaignNotFound')}</CardTitle>
              <CardDescription>
                {t('workforceReadiness.campaignWorkspace.campaignNotFoundDesc')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button asChild>
                <Link to="/workforce-readiness/awareness-campaigns">
                  {t('workforceReadiness.campaignWorkspace.backToAwarenessCampaigns')}
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="mx-auto max-w-[1300px] space-y-6">
        <Button variant="ghost" size="sm" asChild className="-ml-2">
          <Link to="/workforce-readiness/awareness-campaigns">
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('workforceReadiness.campaignWorkspace.awarenessCampaigns')}
          </Link>
        </Button>

        <Card className="sticky top-2 z-10 border-2">
          <CardContent className="space-y-4 pt-6">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                  {t('workforceReadiness.campaignWorkspace.awarenessWorkspace')}
                </p>
                <h1 className="text-2xl font-semibold tracking-tight">{draft.campaignName}</h1>
                <p className="text-sm text-muted-foreground">
                  {draft.campaignType || t('workforceReadiness.campaignWorkspace.addCampaignType')} · {formatDate(draft.startDate)} {t('workforceReadiness.campaignWorkspace.to')}{' '}
                  {formatDate(draft.endDate)}
                </p>
              </div>

              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <div className="space-y-1">
                  <Label className="text-xs">{t('workforceReadiness.campaignWorkspace.statusLabel')}</Label>
                  <Select
                    value={draft.status}
                    options={[
                      { value: 'planned', label: t('workforceReadiness.campaignWorkspace.planned') },
                      { value: 'active', label: t('workforceReadiness.campaignWorkspace.active') },
                      { value: 'completed', label: t('workforceReadiness.campaignWorkspace.completed') },
                    ]}
                    onChange={(value) => {
                      updateDraft((current) => ({
                        ...current,
                        status: value as CampaignStatus,
                      }));
                    }}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">{t('workforceReadiness.campaignWorkspace.startLabel')}</Label>
                  <Input
                    type="date"
                    value={draft.startDate}
                    onChange={(event) => {
                      const nextDate = event.target.value;
                      updateDraft((current) => ({
                        ...current,
                        startDate: nextDate,
                        scheduleItems: current.scheduleItems.map((item) => ({
                          ...item,
                          activityDate: item.activityDate || nextDate,
                        })),
                      }));
                    }}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">{t('workforceReadiness.campaignWorkspace.endLabel')}</Label>
                  <Input
                    type="date"
                    value={draft.endDate}
                    onChange={(event) => {
                      const nextDate = event.target.value;
                      updateDraft((current) => ({
                        ...current,
                        endDate: nextDate,
                      }));
                    }}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">{t('workforceReadiness.campaignWorkspace.participantsLabel')}</Label>
                  <div className="flex h-9 items-center rounded-md border px-3 text-sm font-medium">
                    {String(draft.selectedParticipantIds.length)}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={getCampaignStatusBadgeVariant(draft.status)}>
                {toLabel(draft.status)}
              </Badge>
              <Badge variant="outline">
                {t('workforceReadiness.campaignWorkspace.controlsLinked', { count: draft.selectedControlIds.length })}
              </Badge>
              <Badge variant="outline">
                {t('workforceReadiness.campaignWorkspace.evidenceEntries', {
                  count: campaign.evidenceCount ?? 0,
                })}
              </Badge>
              <Badge variant="outline">{t('workforceReadiness.campaignWorkspace.links', { count: draft.resourceLinks.length })}</Badge>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                onClick={() => {
                  void saveCampaign();
                }}
                disabled={updateCampaignMutation.isPending}
              >
                <Save className="mr-2 h-4 w-4" />
                {updateCampaignMutation.isPending
                  ? 'Saving...'
                  : t('workforceReadiness.campaignWorkspace.saveChanges')}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  void navigate({
                    to: '/audit/evidence-upload',
                    search: {
                      linkedObjectType: 'awareness_campaign',
                      linkedObjectId: campaign.id,
                      complianceContext: `Awareness Campaign: ${draft.campaignName}`,
                    },
                  });
                }}
              >
                <FileUp className="mr-2 h-4 w-4" />
                {t('workforceReadiness.campaignWorkspace.addEvidence')}
              </Button>
              <Button variant="outline" asChild>
                <Link to="/audit/evidence-library">{t('workforceReadiness.campaignWorkspace.evidenceLibrary')}</Link>
              </Button>
            </div>

            {saveError ? (
              <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                {saveError}
              </p>
            ) : null}
            {saveNotice ? (
              <p className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
                {saveNotice}
              </p>
            ) : null}
          </CardContent>
        </Card>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="h-auto w-full flex-wrap justify-start gap-1 bg-transparent p-0">
            <TabsTrigger value="overview">{t('workforceReadiness.campaignWorkspace.overviewTab')}</TabsTrigger>
            <TabsTrigger value="participants">{t('workforceReadiness.campaignWorkspace.participantsTab')}</TabsTrigger>
            <TabsTrigger value="schedule-links">{t('workforceReadiness.campaignWorkspace.scheduleLinksTab')}</TabsTrigger>
            <TabsTrigger value="evidence">{t('workforceReadiness.campaignWorkspace.evidenceTab')}</TabsTrigger>
            <TabsTrigger value="controls">{t('workforceReadiness.campaignWorkspace.controlsTab')}</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>{t('workforceReadiness.campaignWorkspace.campaignOverview')}</CardTitle>
                <CardDescription>
                  {t('workforceReadiness.campaignWorkspace.campaignOverviewDesc')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="campaign-workspace-name">{t('workforceReadiness.campaignWorkspace.campaignNameLabel')}</Label>
                    <Input
                      id="campaign-workspace-name"
                      value={draft.campaignName}
                      onChange={(event) => {
                        const nextValue = event.target.value;
                        updateDraft((current) => ({
                          ...current,
                          campaignName: nextValue,
                        }));
                      }}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="campaign-workspace-type">{t('workforceReadiness.campaignWorkspace.campaignTypeLabel')}</Label>
                    <Input
                      id="campaign-workspace-type"
                      value={draft.campaignType}
                      onChange={(event) => {
                        const nextValue = event.target.value;
                        updateDraft((current) => ({
                          ...current,
                          campaignType: nextValue,
                        }));
                      }}
                    />
                  </div>
                </div>

                <label className="inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm">
                  <Checkbox
                    checked={draft.status === 'completed'}
                    onCheckedChange={(checked) => {
                      updateDraft((current) => ({
                        ...current,
                        status: checked === true ? 'completed' : 'active',
                      }));
                    }}
                  />
                  {t('workforceReadiness.campaignWorkspace.markCampaignCompleted')}
                </label>

                <div className="space-y-1.5">
                  <Label>Evidence</Label>
                  <p className="rounded-md border border-dashed px-3 py-2 text-sm text-muted-foreground">
                    Evidence is linked from the Evidence tab and audit evidence library.
                  </p>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="campaign-workspace-description">{t('workforceReadiness.campaignWorkspace.descriptionLabel')}</Label>
                  <Textarea
                    id="campaign-workspace-description"
                    rows={4}
                    value={draft.description}
                    onChange={(event) => {
                      const nextValue = event.target.value;
                      updateDraft((current) => ({
                        ...current,
                        description: nextValue,
                      }));
                    }}
                    placeholder={t('workforceReadiness.campaignWorkspace.descriptionPlaceholder')}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="participants" className="space-y-4">
            <div className="grid gap-4 xl:grid-cols-2">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">{t('workforceReadiness.campaignWorkspace.employees')}</CardTitle>
                  <CardDescription>{t('workforceReadiness.campaignWorkspace.employeesDesc')}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Input
                    value={participantSearch}
                    onChange={(event) => {
                      setParticipantSearch(event.target.value);
                    }}
                    placeholder={t('workforceReadiness.campaignWorkspace.searchEmployees')}
                    disabled={!canManageAssignments}
                  />
                  <div className="max-h-72 space-y-2 overflow-auto rounded-md border p-2">
                    {!canManageAssignments ? (
                      <p className="text-sm text-muted-foreground">
                        {t('workforceReadiness.campaignWorkspace.selectOrgToLoadEmployees')}
                      </p>
                    ) : membersLoading ? (
                      <p className="text-sm text-muted-foreground">{t('workforceReadiness.campaignWorkspace.loadingParticipants')}</p>
                    ) : filteredMembers.length === 0 ? (
                      <p className="text-sm text-muted-foreground">{t('workforceReadiness.campaignWorkspace.noMatchingEmployees')}</p>
                    ) : (
                      filteredMembers.map((member) => {
                        const checked = draft.selectedParticipantIds.includes(member.userId);
                        return (
                          <label
                            key={member.userId}
                            className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 hover:bg-muted"
                          >
                            <Checkbox
                              checked={checked}
                              onCheckedChange={(value) => {
                                upsertParticipantSelection(member.userId, value === true);
                              }}
                            />
                            <span className="text-sm">
                              {getMemberDisplayName(member)}
                              <span className="ml-1 text-xs text-muted-foreground">
                                ({member.email})
                              </span>
                            </span>
                          </label>
                        );
                      })
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">{t('workforceReadiness.campaignWorkspace.participationStatus')}</CardTitle>
                  <CardDescription>
                    {t('workforceReadiness.campaignWorkspace.participationStatusDesc')}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {draft.selectedParticipantIds.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      {t('workforceReadiness.campaignWorkspace.selectParticipantsToTrack')}
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {draft.selectedParticipantIds.map((userId) => {
                        const member = membersByUserId.get(userId);
                        const displayName = getMemberDisplayName(member, userId);
                        const participant = draft.participantState[userId] ?? {
                          participationStatus: 'pending' as const,
                          result: 'Pending',
                        };

                        return (
                          <div key={userId} className="rounded-md border p-3">
                            <p className="mb-2 text-sm font-medium">{displayName}</p>
                            <div className="grid gap-2 md:grid-cols-2">
                              <Select
                                value={participant.participationStatus}
                                options={[
                                  { value: 'pending', label: t('workforceReadiness.campaignWorkspace.pending') },
                                  { value: 'participated', label: t('workforceReadiness.campaignWorkspace.participated') },
                                  { value: 'failed', label: t('workforceReadiness.campaignWorkspace.failed') },
                                ]}
                                onChange={(value) => {
                                  updateParticipantState(userId, {
                                    participationStatus: value as ParticipationStatus,
                                  });
                                }}
                              />
                              <Input
                                value={participant.result}
                                onChange={(event) => {
                                  updateParticipantState(userId, {
                                    result: event.target.value,
                                  });
                                }}
                                placeholder={t('workforceReadiness.campaignWorkspace.resultPlaceholder')}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="schedule-links" className="space-y-4">
            <div className="grid gap-4 xl:grid-cols-2">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle>{t('workforceReadiness.campaignWorkspace.schedule')}</CardTitle>
                    <CardDescription>
                      {t('workforceReadiness.campaignWorkspace.scheduleDesc')}
                    </CardDescription>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      updateDraft((current) => ({
                        ...current,
                        scheduleItems: [
                          ...current.scheduleItems,
                          createCampaignScheduleDraft(current.startDate),
                        ],
                      }));
                    }}
                  >
                    <Plus className="mr-1 h-3.5 w-3.5" />
                    {t('workforceReadiness.campaignWorkspace.addItem')}
                  </Button>
                </CardHeader>
                <CardContent className="space-y-3">
                  {draft.scheduleItems.length === 0 ? (
                    <p className="text-sm text-muted-foreground">{t('workforceReadiness.campaignWorkspace.noScheduleItems')}</p>
                  ) : (
                    draft.scheduleItems.map((item) => (
                      <div key={item.id} className="rounded-md border p-3">
                        <div className="grid gap-2 md:grid-cols-3">
                          <Input
                            value={item.title}
                            onChange={(event) => {
                              updateScheduleItem(item.id, { title: event.target.value });
                            }}
                            placeholder={t('workforceReadiness.campaignWorkspace.activityPlaceholder')}
                          />
                          <Input
                            type="date"
                            value={item.activityDate}
                            onChange={(event) => {
                              updateScheduleItem(item.id, { activityDate: event.target.value });
                            }}
                          />
                          <Input
                            value={item.channel}
                            onChange={(event) => {
                              updateScheduleItem(item.id, { channel: event.target.value });
                            }}
                            placeholder={t('workforceReadiness.campaignWorkspace.channelPlaceholder')}
                          />
                        </div>
                        <Textarea
                          className="mt-2"
                          rows={2}
                          value={item.notes}
                          onChange={(event) => {
                            updateScheduleItem(item.id, { notes: event.target.value });
                          }}
                          placeholder={t('workforceReadiness.campaignWorkspace.executionNotesPlaceholder')}
                        />
                        <div className="mt-2 flex justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              updateDraft((current) => ({
                                ...current,
                                scheduleItems: current.scheduleItems.filter(
                                  (schedule) => schedule.id !== item.id
                                ),
                              }));
                            }}
                          >
                            <Trash2 className="mr-1 h-3.5 w-3.5" />
                            {t('workforceReadiness.campaignWorkspace.remove')}
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle>{t('workforceReadiness.campaignWorkspace.linksTitle')}</CardTitle>
                    <CardDescription>
                      {t('workforceReadiness.campaignWorkspace.linksDesc')}
                    </CardDescription>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      updateDraft((current) => ({
                        ...current,
                        resourceLinks: [...current.resourceLinks, createCampaignResourceDraft()],
                      }));
                    }}
                  >
                    <Plus className="mr-1 h-3.5 w-3.5" />
                    {t('workforceReadiness.campaignWorkspace.addLink')}
                  </Button>
                </CardHeader>
                <CardContent className="space-y-3">
                  {draft.resourceLinks.length === 0 ? (
                    <p className="text-sm text-muted-foreground">{t('workforceReadiness.campaignWorkspace.noLinksAttached')}</p>
                  ) : (
                    draft.resourceLinks.map((link) => (
                      <div key={link.id} className="rounded-md border p-3">
                        <div className="grid gap-2 md:grid-cols-[1fr_1fr_auto]">
                          <Input
                            value={link.label}
                            onChange={(event) => {
                              updateResourceLink(link.id, { label: event.target.value });
                            }}
                            placeholder={t('workforceReadiness.campaignWorkspace.labelPlaceholder')}
                          />
                          <Input
                            value={link.url}
                            onChange={(event) => {
                              updateResourceLink(link.id, { url: event.target.value });
                            }}
                            placeholder="https://..."
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              updateDraft((current) => ({
                                ...current,
                                resourceLinks: current.resourceLinks.filter(
                                  (item) => item.id !== link.id
                                ),
                              }));
                            }}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                        {link.url ? (
                          <a
                            href={link.url}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-2 inline-block text-xs text-primary hover:underline"
                          >
                            {t('workforceReadiness.campaignWorkspace.openLink')}
                          </a>
                        ) : null}
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="evidence" className="space-y-4">
            <LinkedEvidencePanel
              linkedObjectType="awareness_campaign"
              linkedObjectId={campaign.id}
              linkedObjectName={`Awareness Campaign: ${draft.campaignName}`}
              emptyMessage={t('workforceReadiness.campaignWorkspace.noEvidenceEntries')}
            />
          </TabsContent>

          <TabsContent value="controls" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>{t('workforceReadiness.campaignWorkspace.linkedControls')}</CardTitle>
                <CardDescription>{t('workforceReadiness.campaignWorkspace.linkedControlsDesc')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <Input
                  value={controlSearch}
                  onChange={(event) => {
                    setControlSearch(event.target.value);
                  }}
                  placeholder={t('workforceReadiness.campaignWorkspace.searchControls')}
                  disabled={!canManageAssignments}
                />
                <div className="max-h-72 space-y-2 overflow-auto rounded-md border p-2">
                  {!canManageAssignments ? (
                    <p className="text-sm text-muted-foreground">
                      {t('workforceReadiness.campaignWorkspace.selectOrgToLoadControls')}
                    </p>
                  ) : controlsLoading ? (
                    <p className="text-sm text-muted-foreground">{t('workforceReadiness.campaignWorkspace.loadingControls')}</p>
                  ) : filteredControls.length === 0 ? (
                    <p className="text-sm text-muted-foreground">{t('workforceReadiness.campaignWorkspace.noMatchingControls')}</p>
                  ) : (
                    filteredControls.map((control) => {
                      const checked = draft.selectedControlIds.includes(control.id);
                      return (
                        <label
                          key={control.id}
                          className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 hover:bg-muted"
                        >
                          <Checkbox
                            checked={checked}
                            onCheckedChange={(value) => {
                              upsertControlSelection(control.id, value === true);
                            }}
                          />
                          <span className="text-sm">{getControlLabel(control, control.id)}</span>
                        </label>
                      );
                    })
                  )}
                </div>

                <div className="space-y-1">
                  <p className="text-sm font-medium">{t('workforceReadiness.campaignWorkspace.currentlyLinked')}</p>
                  {draft.selectedControlIds.length === 0 ? (
                    <p className="text-sm text-muted-foreground">{t('workforceReadiness.campaignWorkspace.noControlsLinked')}</p>
                  ) : (
                    <div className="space-y-1.5">
                      {draft.selectedControlIds.map((controlId) => {
                        const control = controlsById.get(controlId);
                        return (
                          <Link
                            key={controlId}
                            to="/controls/$controlId"
                            params={{ controlId }}
                            className="block text-sm text-primary hover:underline"
                          >
                            {getControlLabel(control, controlId)}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function buildCampaignWorkspaceDraft(campaign: AwarenessCampaign): CampaignWorkspaceDraft {
  const participantState: Record<string, ParticipantStateDraft> = {};
  for (const participant of campaign.participants) {
    participantState[participant.userId] = {
      participationStatus: participant.participationStatus,
      result: participant.result,
    };
  }

  return {
    campaignName: campaign.campaignName,
    campaignType: campaign.campaignType,
    description: campaign.description,
    status: campaign.status,
    startDate: campaign.startDate,
    endDate: campaign.endDate,
    selectedParticipantIds: campaign.participants.map((participant) => participant.userId),
    participantState,
    selectedControlIds: [...campaign.linkedControlIds],
    scheduleItems: campaign.scheduleItems.map((item) => ({ ...item })),
    resourceLinks: campaign.resourceLinks.map((item) => ({ ...item })),
  };
}

function toComparableCampaign(campaign: AwarenessCampaign) {
  return {
    campaignName: campaign.campaignName,
    campaignType: campaign.campaignType,
    description: campaign.description,
    status: campaign.status,
    startDate: campaign.startDate,
    endDate: campaign.endDate,
    participants: campaign.participants,
    linkedControlIds: campaign.linkedControlIds,
    scheduleItems: campaign.scheduleItems,
    resourceLinks: campaign.resourceLinks,
  };
}

function toComparableCampaignDraft(draft: CampaignWorkspaceDraft) {
  return {
    campaignName: draft.campaignName,
    campaignType: draft.campaignType,
    description: draft.description,
    status: draft.status,
    startDate: draft.startDate,
    endDate: draft.endDate,
    participants: draft.selectedParticipantIds.map((userId) => ({
      userId,
      participationStatus: draft.participantState[userId]?.participationStatus ?? 'pending',
      result: draft.participantState[userId]?.result ?? 'Pending',
    })),
    linkedControlIds: draft.selectedControlIds,
    scheduleItems: draft.scheduleItems,
    resourceLinks: draft.resourceLinks,
  };
}

function createCampaignScheduleDraft(activityDate: string): CampaignScheduleItem {
  return {
    id: crypto.randomUUID(),
    title: '',
    activityDate,
    channel: '',
    notes: '',
  };
}

function createCampaignResourceDraft(): CampaignResourceLink {
  return {
    id: crypto.randomUUID(),
    label: '',
    url: '',
  };
}

function uniqueValues(values: string[]): string[] {
  return Array.from(new Set(values));
}

function getCampaignStatusBadgeVariant(
  status: CampaignStatus
): 'default' | 'secondary' | 'outline' | 'destructive' {
  if (status === 'active') {
    return 'default';
  }
  if (status === 'completed') {
    return 'secondary';
  }
  return 'outline';
}

function getMemberDisplayName(
  member: OrganizationMember | undefined,
  fallbackUserId?: string
): string {
  if (!member) {
    return fallbackUserId ?? 'Unknown user';
  }

  if (member.displayName) {
    return member.displayName;
  }

  const firstName = member.firstName?.trim() ?? '';
  const lastName = member.lastName?.trim() ?? '';
  const fullName = `${firstName} ${lastName}`.trim();

  if (fullName) {
    return fullName;
  }

  return member.email;
}

function getControlLabel(control: Control | undefined, fallbackControlId: string): string {
  if (!control) {
    return fallbackControlId;
  }

  return `${control.controlCode} - ${control.title}`;
}

function toLabel(value: string): string {
  return value.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatDate(value: string): string {
  if (!value) {
    return 'Not scheduled';
  }
  return new Date(value).toLocaleDateString();
}
